import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Pricing tiers — Strapi sections.pricing-section + components.pricing-cards +
// atoms.points. Header + a responsive grid of plan cards (name, price/frequency,
// description, feature list, highlighted variant).
export const pricingTableSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  tiers: z
    .array(
      z.object({
        name: z.string().min(1),
        description: z.string().optional(),
        price: z.string().optional(),
        frequency: z.string().optional(),
        features: z.array(z.string()).default([]),
        highlighted: z.boolean().optional(),
      }),
    )
    .min(1),
});

type PricingTableProps = z.infer<typeof pricingTableSchema>;

const Section = styled.section`
  padding: 4rem 2rem;

  @media (min-width: 48em) {
    padding: 5rem 10rem;
  }
  @media (min-width: 90em) {
    padding: 5rem 25rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  text-align: center;
  max-width: 90%;

  @media (min-width: 48em) {
    font-size: 4rem;
  }
`;

const Description = styled.p`
  margin: 1.6rem 0 0;
  color: #555555;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  line-height: 2.8rem;
  text-align: center;
  max-width: 65rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.6rem;
  grid-template-columns: 1fr;
  margin-top: 5rem;

  @media (min-width: 48em) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled.div<{ $highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  background: ${({ $highlighted }) => ($highlighted ? '#47d7ac' : '#f9f9f9')};
  border: 1px solid ${({ $highlighted }) => ($highlighted ? '#007672' : 'transparent')};
`;

const Name = styled.h3`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 2rem;
`;

const CardDesc = styled.p`
  margin: 2rem 0 0;
  color: #555555;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;
`;

const Features = styled.ul`
  list-style: none;
  margin: 2rem 0 0;
  padding: 0;
  display: grid;
  gap: 1rem;
`;

const Feature = styled.li`
  display: grid;
  grid-template-columns: 1.5rem auto;
  gap: 1rem;
  align-items: start;
  color: #373737;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;

  &::before {
    content: '✓';
    color: #1a1b1f;
    font-weight: 700;
  }
`;

const Foot = styled.div`
  margin-top: auto;
  padding-top: 4rem;
`;

const Price = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 4.8rem;
`;

const Frequency = styled.p`
  margin: 0;
  color: #555555;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
`;

const Contact = styled.a`
  margin: 0;
  color: #012169;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
  cursor: pointer;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

function PricingTable({ title, description, tiers }: PricingTableProps) {
  return (
    <Section>
      {title || description ? (
        <Header>
          {title ? <Title>{title}</Title> : null}
          {description ? <Description>{description}</Description> : null}
        </Header>
      ) : null}
      <Grid>
        {tiers.map((tier, i) => (
          <Card key={`${tier.name}-${i}`} $highlighted={!!tier.highlighted}>
            <Name>{tier.name}</Name>
            {tier.description ? <CardDesc>{tier.description}</CardDesc> : null}
            {tier.features.length > 0 ? (
              <Features>
                {tier.features.map((f, j) => (
                  <Feature key={j}>{f}</Feature>
                ))}
              </Features>
            ) : null}
            <Foot>
              {tier.price ? (
                <>
                  <Price>{tier.price}</Price>
                  {tier.frequency ? <Frequency>{tier.frequency}</Frequency> : null}
                </>
              ) : (
                <Contact href="#contact">Contact Us for Pricing</Contact>
              )}
            </Foot>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const pricingTableBlock: BlockDefinition<PricingTableProps> = {
  type: 'pricingTable',
  schema: pricingTableSchema,
  Renderer: PricingTable,
};
